import { TranslationService } from "../services/TranslationService";

/**
 * URLs absolutas para icono/badge de notificaciones del navegador (Intiza / public).
 */
export function getBrowserNotificationIconUrl(): string {
  if (typeof window === "undefined") return "";
  const base = process.env.PUBLIC_URL ?? "";
  const path = `${base.replace(/\/$/, "")}/logo512.png`;
  try {
    return new URL(path, window.location.origin).href;
  } catch {
    return `${window.location.origin}${path.startsWith("/") ? "" : "/"}${path}`;
  }
}

export function getBrowserNotificationBadgeUrl(): string {
  if (typeof window === "undefined") return "";
  const base = process.env.PUBLIC_URL ?? "";
  const path = `${base.replace(/\/$/, "")}/logo192.png`;
  try {
    return new URL(path, window.location.origin).href;
  } catch {
    return `${window.location.origin}${path.startsWith("/") ? "" : "/"}${path}`;
  }
}

/**
 * URL absoluta a abrir al hacer clic en la notificación (la lee el service worker en data.url).
 * Con personId abre el detalle del cliente; sin él, la raíz del idioma actual.
 */
export function getBrowserNotificationClickUrl(
  personId?: number | string
): string {
  if (typeof window === "undefined") return "";
  const lang = TranslationService.currentLanguage;
  const base = (process.env.PUBLIC_URL ?? "").replace(/\/$/, "");
  const path = personId
    ? `${base}/${lang}/client/details?id=${personId}`
    : `${base}/${lang}/`;
  return new URL(path, window.location.origin).href;
}

/** localStorage: preferencias de qué eventos disparan notificación del sistema */
export const BROWSER_NOTIFY_EVENTS_STORAGE_KEY = "browserNotify_events";

/** localStorage: si ya se le mostró el prompt de permiso al usuario */
export const BROWSER_NOTIFY_PROMPTED_KEY = "browserNotify_prompted";

export function hasBeenNotificationPrompted(): boolean {
  if (typeof window === "undefined") return true;
  return localStorage.getItem(BROWSER_NOTIFY_PROMPTED_KEY) === "true";
}

export function markNotificationPrompted(): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(BROWSER_NOTIFY_PROMPTED_KEY, "true");
}

export type BrowserNotifyEventPrefs = {
  /** Mensajes WhatsApp entrantes (socket CallReceived, Type 6) */
  whatsappIncoming: boolean;
};

export const DEFAULT_BROWSER_NOTIFY_PREFS: BrowserNotifyEventPrefs = {
  whatsappIncoming: true
};

export function loadBrowserNotifyEventPrefs(): BrowserNotifyEventPrefs {
  if (typeof window === "undefined") return { ...DEFAULT_BROWSER_NOTIFY_PREFS };
  try {
    const raw = localStorage.getItem(BROWSER_NOTIFY_EVENTS_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_BROWSER_NOTIFY_PREFS };
    const parsed = JSON.parse(raw) as Partial<BrowserNotifyEventPrefs>;
    return {
      whatsappIncoming:
        typeof parsed.whatsappIncoming === "boolean"
          ? parsed.whatsappIncoming
          : DEFAULT_BROWSER_NOTIFY_PREFS.whatsappIncoming
    };
  } catch {
    return { ...DEFAULT_BROWSER_NOTIFY_PREFS };
  }
}

export function saveBrowserNotifyEventPrefs(
  prefs: BrowserNotifyEventPrefs
): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(
    BROWSER_NOTIFY_EVENTS_STORAGE_KEY,
    JSON.stringify(prefs)
  );
}

/** Payload típico de CallReceived (WhatsApp / inbox) */
export function isWhatsAppCallReceivedPayload(payload: unknown): boolean {
  if (!payload || typeof payload !== "object") return false;
  const p = payload as Record<string, unknown>;
  const type = p.Type ?? p.type;
  return type === 6 || type === "6" || p.typeName === "Whatsapp";
}

/** CallReceived sin datos (p. ej. disparado por /api/users/notifyCallReceived) */
export function isEmptyCallReceivedPayload(payload: unknown): boolean {
  if (payload === null || payload === undefined) return true;
  return typeof payload === "object" && Object.keys(payload).length === 0;
}
